import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import {
    routeKey,
    saveScrollPosition,
    getScrollPosition,
    restoreScrollPosition,
    markPopRestore,
    clearPopRestore,
    initScrollPositionCapture,
} from '../utils/scrollRestoration';

let captureInitialized = false;

/**
 * True until the first restore effect runs. A full page load / hard refresh
 * remounts the whole app, so this resets to true — letting us force the top of
 * the page on refresh instead of restoring the previous (possibly footer)
 * scroll position. In-app back/forward (POP) keeps the app mounted, so this
 * stays false and normal scroll restoration applies.
 */
let isInitialLoad = true;

/**
 * Saves scroll per route and restores on browser back/forward (POP).
 * Forward navigation (PUSH/REPLACE) scrolls to top.
 */
export default function ScrollRestoration() {
    const { pathname, search } = useLocation();
    const navigationType = useNavigationType();
    const key = routeKey(pathname, search);
    const cancelRestoreRef = useRef(null);
    const lastScrollYRef = useRef(0);

    useEffect(() => {
        if (!captureInitialized) {
            initScrollPositionCapture(() =>
                routeKey(window.location.pathname, window.location.search)
            );
            captureInitialized = true;
        }
    }, []);

    useEffect(() => {
        lastScrollYRef.current = window.scrollY;

        const onScroll = () => {
            lastScrollYRef.current = window.scrollY;
            saveScrollPosition(key, window.scrollY);
        };

        window.addEventListener('scroll', onScroll, { passive: true });

        return () => {
            window.removeEventListener('scroll', onScroll);
            const y = Math.max(lastScrollYRef.current, window.scrollY);
            saveScrollPosition(key, y);
        };
    }, [key]);

    useEffect(() => {
        cancelRestoreRef.current?.();
        cancelRestoreRef.current = null;

        // First run after a fresh page load / hard refresh: always start at the
        // top. Do NOT restore the previous scroll position (that behaviour is
        // only for in-app browser back/forward).
        if (isInitialLoad) {
            isInitialLoad = false;
            clearPopRestore(key);
            applyScrollTop();
            return;
        }

        if (navigationType === 'POP') {
            const saved = getScrollPosition(key);
            if (saved != null && saved > 0) {
                markPopRestore(key);
                cancelRestoreRef.current = restoreScrollPosition(key);
                return;
            }
        }

        clearPopRestore(key);
        applyScrollTop();
    }, [key, navigationType]);

    return null;
}

function applyScrollTop() {
    const html = document.documentElement;
    const body = document.body;
    const prevHtml = html.style.scrollBehavior;
    const prevBody = body.style.scrollBehavior;
    html.style.scrollBehavior = 'auto';
    body.style.scrollBehavior = 'auto';
    window.scrollTo(0, 0);
    html.style.scrollBehavior = prevHtml;
    body.style.scrollBehavior = prevBody;
}
